import { Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Kpi, KpiKeys, KpiTimeCycleChartData } from '@models';

import { KpiExpandedViewComponent } from '../kpi-expanded-view/kpi-expanded-view.component';
interface MilestoneStatusSummaryItem {
  label: string;
  value: number;
}
const DEFAULT_TIME_CYCLE_CHART_DATA: KpiTimeCycleChartData = {
  identifier: 'timeCycleChart',
  title: 'Average cycle time',
  tooltipText:
    'Cycle time is the elapsed time from the first milestone’s start to the second milestone’s completion.',
  labels: [],
  offset: [],
  timeWeeks: [],
  category: [],
  cycleCount: 0
};

@Component({
  selector: 'portfolio-kpi-expanded-panel',
  templateUrl: './portfolio-kpi-expanded-panel.component.html',
  styleUrl: './portfolio-kpi-expanded-panel.component.scss'
})
export class PortfolioKpiExpandedPanelComponent implements OnChanges {
  private dialog = inject(MatDialog);

  @Input() expanded = false;
  @Input() selectedKpi?: { key: KpiKeys; kpi: Kpi };
  @Input() isHeaderLoading = false;
  @Input() isCycleLoading = false;
  @Input() isSummaryLoading = false;
  @Input() columnTitleMappings: Record<string, string> = {};
  @Input() portfolioExpandedStudiesText = '--';
  @Input() portfolioKpiSecondaryValue = '--';
  @Input() portfolioKpiDisplayValue = '--';
  @Input() milestoneStatusSummaryData: MilestoneStatusSummaryItem[] = [];
  @Input() timeCycleChartData: KpiTimeCycleChartData = DEFAULT_TIME_CYCLE_CHART_DATA;
  @Input() kpiFiltersApplied: Record<string, string[]> = {};
  @Input() totalKpiFiltersApplied = 0;
  hasRenderedPanel = false;

  @Output() collapsed = new EventEmitter<void>();
  @Output() closedAll = new EventEmitter<void>();
  @Output() summaryStatusFilterToggle = new EventEmitter<string>();
  @Output() kpiRemoveFilter = new EventEmitter<{ field: string; value: string }>();
  @Output() kpiClearAllFilters = new EventEmitter<void>();

  ngOnChanges(): void {
    if (this.expanded && this.selectedKpi) {
      this.hasRenderedPanel = true;
    }
  }

  onCollapsed(): void {
    this.collapsed.emit();
  }

  onClosedAll(): void {
    this.closedAll.emit();
  }

  openExpandedChart(title: string, chartCardType: 'cycle' | 'summary'): void {
    this.dialog.open(KpiExpandedViewComponent, {
      data: {
        title,
        type: 'chart',
        chartCardType,
        isLoading: chartCardType === 'cycle' ? this.isCycleLoading : this.isSummaryLoading,
        timeCycleChartData: chartCardType === 'cycle' ? this.timeCycleChartData : undefined,
        columnTitleMappings: this.columnTitleMappings,
        kpiFiltersApplied: this.kpiFiltersApplied,
        totalKpiFiltersApplied: this.totalKpiFiltersApplied,
        getMilestoneStatusSummaryData: () => this.milestoneStatusSummaryData,
        getTimeCycleChartData: () => this.timeCycleChartData,
        getIsLoading: () =>
          chartCardType === 'cycle' ? this.isCycleLoading : this.isSummaryLoading,
        getKpiFiltersApplied: () => this.kpiFiltersApplied,
        getTotalKpiFiltersApplied: () => this.totalKpiFiltersApplied,
        onSummaryStatusClick: (status: string) => this.summaryStatusFilterToggle.emit(status),
        onKpiRemoveFilter: (payload: { field: string; value: string }) =>
          this.kpiRemoveFilter.emit(payload),
        onKpiClearAllFilters: () => this.kpiClearAllFilters.emit(),
        milestoneStatusSummaryData:
          chartCardType === 'summary' ? this.milestoneStatusSummaryData : []
      },
      panelClass: 'fullscreen-dialog',
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh'
    });
  }

  openExpandedTable(): void {
    this.dialog.open(KpiExpandedViewComponent, {
      data: {
        title: 'Milestone summary table',
        type: 'table',
        columnTitleMappings: this.columnTitleMappings,
        kpiFiltersApplied: this.kpiFiltersApplied,
        totalKpiFiltersApplied: this.totalKpiFiltersApplied,
        getKpiFiltersApplied: () => this.kpiFiltersApplied,
        getTotalKpiFiltersApplied: () => this.totalKpiFiltersApplied,
        onKpiRemoveFilter: (payload: { field: string; value: string }) =>
          this.kpiRemoveFilter.emit(payload),
        onKpiClearAllFilters: () => this.kpiClearAllFilters.emit()
      },
      panelClass: 'fullscreen-dialog',
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh'
    });
  }
}
